import * as React from "react";

import { t } from "@impero-com/common/utils/i18n";
import {
    Button,
    ButtonGroup,
    ButtonSpec,
    InformationModal,
} from "@impero-com/ui";

import { Drawer } from "components/drawer";
import { InfoBox } from "components/infoBox";
import noAccessSvg from "img/no-access-icon.svg";
import {
    FormCreate,
    FormRootComponentProps,
    WrappedFormUtils,
    hasError,
    ObjectError,
} from "utils/form";

import * as theme from "./theme.css";

interface ResourcesDrawerProps<
    FormValues,
> extends FormRootComponentProps<FormValues> {
    title: string;
    readOnly: boolean;
    selectedIds: Array<string>;
    readOnlyInfo: string;
    renderBody: (
        form: WrappedFormUtils<FormValues, FormValues>
    ) => React.ReactNode;
    confirmationTitle: string;
    confirmationText: string;
    onSave: (
        form: WrappedFormUtils<FormValues, FormValues>,
        closeDialog: () => void
    ) => void;
    onCancel: () => void;
    width?: "DEFAULT" | "LARGE";
}

interface ResourcesDrawerState {
    confirmationDialog: boolean;
}

class ResourcesDrawer<FormValues> extends React.PureComponent<
    ResourcesDrawerProps<FormValues>,
    ResourcesDrawerState
> {
    constructor(props: ResourcesDrawerProps<FormValues>) {
        super(props);
        this.state = {
            confirmationDialog: false,
        };
    }

    render = () => {
        const { title, onCancel, readOnly, selectedIds, form, width } =
            this.props;

        const buttons: ButtonSpec[] = [];
        if (!readOnly) {
            buttons.push({
                id: "apply-to-all",
                label: t("Apply_To_All", { count: selectedIds.length }),
                type: "primary",
                onClick: () => {
                    form.validateFields({ force: true }, (errors, _data) => {
                        if (errors) {
                            return;
                        }
                        this.setState({ confirmationDialog: true });
                    });
                },
                disabled: hasError(form.getFieldsError() as ObjectError),
            });
        }
        buttons.push({
            id: "drawer-cancel-id",
            label: t("Cancel"),
            type: "link",
            onClick: onCancel,
        });

        return (
            <>
                <Drawer
                    buildHeader={() => title}
                    buildFooter={() => <ButtonGroup buttonSpecs={buttons} />}
                    onCancel={onCancel}
                    width={width}
                >
                    {this.drawInfoBox()}
                    {this.props.renderBody(form)}
                </Drawer>
                {this.drawConfirmationDialog()}
            </>
        );
    };

    drawInfoBox = () => {
        const { readOnly, readOnlyInfo } = this.props;
        if (!readOnly) {
            return null;
        }
        return (
            <div className={theme.resourcesDrawerInfo}>
                <InfoBox
                    text={readOnlyInfo}
                    visible={true}
                    icon={{ svg: noAccessSvg }}
                />
            </div>
        );
    };

    drawConfirmationDialog = () => {
        if (!this.state.confirmationDialog) {
            return null;
        }
        const { confirmationTitle, confirmationText, onSave, form } =
            this.props;
        return (
            <InformationModal
                open
                onClose={() => this.setState({ confirmationDialog: false })}
                type="info"
                icon="save"
                title={confirmationTitle}
                footer={
                    <>
                        <Button
                            onClick={() =>
                                this.setState({ confirmationDialog: false })
                            }
                        >
                            {t("Cancel")}
                        </Button>
                        <Button
                            variant="primary"
                            onClick={() =>
                                onSave(form, () =>
                                    this.setState({ confirmationDialog: false })
                                )
                            }
                        >
                            {t("Save")}
                        </Button>
                    </>
                }
            >
                {confirmationText}
            </InformationModal>
        );
    };
}

const ResourcesDrawerForm = FormCreate<any, ResourcesDrawerProps<any>>()(
    ResourcesDrawer
);

export {
    type ResourcesDrawerProps,
    type ResourcesDrawerState,
    ResourcesDrawerForm as ResourcesDrawer,
};
